import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { selectHasLoadError, selectIsLoadingProject } from '@ff-os/pdp-shared-redux/slices/projectLoadingSlice';
import { selectIsProductDisabled } from '@ff-os/pdp-shared-redux/slices/sceneSlice';
import { Alert } from '@ff-os/shared-react/components/alert/Alert';
import { Button } from '@ff-os/shared-react/components/buttons/button/Button';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid } from '@ff-os/shared-react/components/grid/Grid';
import { Icon } from '@ff-os/shared-react/components/icons/Icon';
import { Spinner } from '@ff-os/shared-react/components/loaders/spinner/Spinner';
import { WindowStackProvider } from '@ff-os/shared-react/context/window-stack/WindowStack';
import { WindowStackItem } from '@ff-os/shared-react/context/window-stack/WindowStackItem';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { selectIsPhotoPrintsWindowOpen, selectShowProjectIsIncompatible, setIsPhotoPrintsWindowOpen } from './_redux-ui';
import { PhotoPrintsWindow } from './components/PhotoPrintsWindow';
import styles from './PhotoPrintsUi.module.scss';

const _PhotoPrintsUi = ({
    isLoadingProject,
    hasLoadError,
    showProjectIsIncompatible,
    isOpen, setOpen,
    isProductDisabled
}: Props) => {

    const { t } = useTranslation();

    const open = (value: boolean) => {
        if (!isLoadingProject && !isProductDisabled) {
            setOpen(value);
        }
    };

    const buttonText = isProductDisabled
        ? t('order_button.temporarily_out_of_stock')
        : t('photo_prints.start_designing');

    return (
        <WindowStackProvider>
            <Grid gap='s' direction='column'>

                {hasLoadError && (
                    <Cell>
                        <Alert icon='alert-triangle-exclamation' type='error'>
                            {t('load_project.error_message')}
                        </Alert>
                    </Cell>
                )}

                {showProjectIsIncompatible && (
                    <Cell>
                        <Alert icon='alert-triangle-exclamation' type='warning'>
                            {t('load_project.incompatible_message')}
                        </Alert>
                    </Cell>
                )}

                <Cell>
                    <Button
                        className='idt-button--product-page'
                        onClick={() => open(true)}
                        disabled={isLoadingProject || isProductDisabled}>
                        {isProductDisabled
                            ? null
                            : isLoadingProject ? (
                                <Spinner color='white' overlaySize='auto' spinnerSize='small'
                                    className={styles.spinner} />
                            ) : (
                                <Icon type='general-ui-brush' size='small' strokeWidth={2}
                                    className={styles.icon} />
                            )}
                        {buttonText}
                    </Button>
                </Cell>
            </Grid>

            {isOpen && (
                <WindowStackItem>
                    <PhotoPrintsWindow close={() => setOpen(false)} />
                </WindowStackItem>
            )}
        </WindowStackProvider>
    );
};

type Props = {
    isLoadingProject: boolean;
    hasLoadError: boolean;
    isOpen: boolean;
    setOpen: (value: boolean) => void;
    showProjectIsIncompatible: boolean;
    isProductDisabled: boolean;
};

const mapStateToProps = (state: PhotoPrintsPdpState) => ({
    isLoadingProject: selectIsLoadingProject(state),
    hasLoadError: selectHasLoadError(state),
    isOpen: selectIsPhotoPrintsWindowOpen(state),
    showProjectIsIncompatible: selectShowProjectIsIncompatible(state),
    isProductDisabled: selectIsProductDisabled(state)
});

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch) => ({
    setOpen: (value: boolean) => dispatch(setIsPhotoPrintsWindowOpen(value))
});

export const PhotoPrintsUi = connect(
    mapStateToProps,
    mapDispatchToProps
)(_PhotoPrintsUi);